/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Calculator, Plus, X, CheckCircle2, AlertTriangle, TrendingDown, TrendingUp, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

// MAT = Matières Azotées Totales (protéines brutes %)
// UFL = Unité Fourragère Lait (énergie / kg MS)
// CB  = Cellulose Brute (fibres %)
// price = DT / kg
const ANIMAL_PROFILES = [
  { id: "brebis_lact", label: "Brebis en lactation", mat: [16, 18], ufl: [0.85, 0.95], cb: [15, 22] },
  { id: "brebis_gest", label: "Brebis en gestation (fin)", mat: [13, 14], ufl: [0.75, 0.85], cb: [18, 25] },
  { id: "brebis_ent", label: "Brebis à l'entretien", mat: [9, 11], ufl: [0.55, 0.70], cb: [22, 30] },
  { id: "agneau", label: "Agneau en croissance", mat: [15, 17], ufl: [0.90, 1.00], cb: [12, 18] },
  { id: "vache_lait", label: "Vache laitière", mat: [16, 18], ufl: [0.85, 0.95], cb: [15, 22] },
  { id: "vache_tarie", label: "Vache tarie", mat: [12, 13], ufl: [0.70, 0.80], cb: [20, 28] },
  { id: "chevre_lact", label: "Chèvre en lactation", mat: [16, 18], ufl: [0.85, 0.95], cb: [15, 22] },
  { id: "lapin", label: "Lapin en engraissement", mat: [15, 17], ufl: [0.95, 1.05], cb: [12, 16] },
  { id: "poule_pond", label: "Poule pondeuse", mat: [16, 17], ufl: [0.95, 1.05], cb: [4, 7] },
  { id: "poulet_chair", label: "Poulet de chair", mat: [20, 22], ufl: [1.00, 1.10], cb: [3, 5] },
];

const PRESET_INGREDIENTS = [
  { id: "orge",      label: "Orge",                  mat: 11, ufl: 1.05, cb: 6,  price: 0.85 },
  { id: "mais",      label: "Maïs",                  mat: 9,  ufl: 1.15, cb: 3,  price: 1.30 },
  { id: "ble_son",   label: "Son de blé",            mat: 15, ufl: 0.75, cb: 11, price: 0.55 },
  { id: "soja",      label: "Tourteau de soja",      mat: 44, ufl: 1.10, cb: 7,  price: 2.20 },
  { id: "tournesol", label: "Tourteau de tournesol", mat: 30, ufl: 0.70, cb: 25, price: 1.10 },
  { id: "luzerne",   label: "Luzerne déshydratée",   mat: 17, ufl: 0.65, cb: 27, price: 0.90 },
  { id: "foin",      label: "Foin",                  mat: 9,  ufl: 0.55, cb: 32, price: 0.40 },
  { id: "paille",    label: "Paille",                mat: 4,  ufl: 0.35, cb: 42, price: 0.20 },
  { id: "feves",     label: "Fèves",                 mat: 25, ufl: 1.05, cb: 8,  price: 1.40 },
  { id: "dreche",    label: "Drêche de brasserie",   mat: 25, ufl: 0.95, cb: 17, price: 0.60 },
  { id: "cmv",       label: "CMV / Minéraux",        mat: 0,  ufl: 0,    cb: 0,  price: 2.50 },
];

const MAX_INGREDIENTS = 4;

// Modèles de rations préprogrammés — proportions calibrées pour atteindre les cibles MAT/UFL
// Chaque modèle pré-remplit le profil animal + les ingrédients du tableau ci-dessous.
const RATION_TEMPLATES = [
  {
    id: "tpl_brebis_lact",
    icon: "🐑",
    label: "Brebis allaitante",
    description: "Production laitière + agneaux",
    color: "#C84B31",
    profileId: "brebis_lact",
    rows: [
      { ingredientId: "orge",    kg: 0.6 },
      { ingredientId: "soja",    kg: 0.3 },
      { ingredientId: "ble_son", kg: 0.4 },
      { ingredientId: "foin",    kg: 0.5 },
    ],
  },
  {
    id: "tpl_brebis_gest",
    icon: "🐑",
    label: "Brebis gestante (fin)",
    description: "2 derniers mois de gestation",
    color: "#D97706",
    profileId: "brebis_gest",
    rows: [
      { ingredientId: "orge",    kg: 0.5 },
      { ingredientId: "soja",    kg: 0.1 },
      { ingredientId: "ble_son", kg: 0.3 },
      { ingredientId: "foin",    kg: 0.6 },
    ],
  },
  {
    id: "tpl_brebis_ent",
    icon: "🐑",
    label: "Brebis à l'entretien",
    description: "Hors lactation / hors gestation",
    color: "#7A5A1C",
    profileId: "brebis_ent",
    rows: [
      { ingredientId: "foin",    kg: 1.0 },
      { ingredientId: "orge",    kg: 0.3 },
      { ingredientId: "ble_son", kg: 0.2 },
    ],
  },
  {
    id: "tpl_agneau_eng",
    icon: "🐑",
    label: "Engraissement agneaux",
    description: "Croissance rapide jusqu'à abattage",
    color: "#E05A3D",
    profileId: "agneau",
    rows: [
      { ingredientId: "orge",    kg: 0.7 },
      { ingredientId: "soja",    kg: 0.2 },
      { ingredientId: "ble_son", kg: 0.1 },
      { ingredientId: "foin",    kg: 0.3 },
    ],
  },
  {
    id: "tpl_belier",
    icon: "🐏",
    label: "Bélier reproducteur",
    description: "Préparation à la lutte",
    color: "#1C3A27",
    profileId: "brebis_gest",
    rows: [
      { ingredientId: "orge",    kg: 0.5 },
      { ingredientId: "soja",    kg: 0.15 },
      { ingredientId: "ble_son", kg: 0.25 },
      { ingredientId: "foin",    kg: 0.6 },
    ],
  },
  {
    id: "tpl_chevre_lact",
    icon: "🐐",
    label: "Chèvre laitière",
    description: "En pleine lactation",
    color: "#1E6BBA",
    profileId: "chevre_lact",
    rows: [
      { ingredientId: "orge",    kg: 0.5 },
      { ingredientId: "soja",    kg: 0.25 },
      { ingredientId: "ble_son", kg: 0.35 },
      { ingredientId: "foin",    kg: 0.5 },
    ],
  },
  {
    id: "tpl_chevreau_eng",
    icon: "🐐",
    label: "Chevreau engraissement",
    description: "Croissance sevrage → abattage",
    color: "#E05A3D",
    profileId: "agneau",
    rows: [
      { ingredientId: "orge",    kg: 0.6 },
      { ingredientId: "soja",    kg: 0.2 },
      { ingredientId: "ble_son", kg: 0.1 },
      { ingredientId: "foin",    kg: 0.25 },
    ],
  },
  {
    id: "tpl_vache_lait",
    icon: "🐄",
    label: "Vache laitière",
    description: "Complément pour 20–25 L/jour",
    color: "#1E6BBA",
    profileId: "vache_lait",
    rows: [
      { ingredientId: "orge",    kg: 1.5 },
      { ingredientId: "soja",    kg: 0.8 },
      { ingredientId: "ble_son", kg: 0.5 },
      { ingredientId: "foin",    kg: 1.2 },
    ],
  },
  {
    id: "tpl_vache_tarie",
    icon: "🐄",
    label: "Vache tarie",
    description: "Préparation au vêlage",
    color: "#7A5A1C",
    profileId: "vache_tarie",
    rows: [
      { ingredientId: "foin",    kg: 2.0 },
      { ingredientId: "ble_son", kg: 0.8 },
      { ingredientId: "orge",    kg: 0.5 },
      { ingredientId: "soja",    kg: 0.2 },
    ],
  },
  {
    id: "tpl_poule_pond",
    icon: "🐔",
    label: "Poule pondeuse",
    description: "Ponte intensive",
    color: "#D97706",
    profileId: "poule_pond",
    rows: [
      { ingredientId: "mais",    kg: 0.6 },
      { ingredientId: "soja",    kg: 0.2 },
      { ingredientId: "ble_son", kg: 0.15 },
      { ingredientId: "cmv",     kg: 0.05 },
    ],
  },
  {
    id: "tpl_poulet_chair",
    icon: "🐔",
    label: "Poulet de chair",
    description: "Finition / croissance",
    color: "#C84B31",
    profileId: "poulet_chair",
    rows: [
      { ingredientId: "mais",    kg: 0.55 },
      { ingredientId: "soja",    kg: 0.35 },
      { ingredientId: "ble_son", kg: 0.05 },
      { ingredientId: "cmv",     kg: 0.05 },
    ],
  },
  {
    id: "tpl_lapin_eng",
    icon: "🐰",
    label: "Lapin engraissement",
    description: "Croissance & finition",
    color: "#1C7A3C",
    profileId: "lapin",
    rows: [
      { ingredientId: "luzerne", kg: 0.30 },
      { ingredientId: "orge",    kg: 0.30 },
      { ingredientId: "soja",    kg: 0.08 },
      { ingredientId: "mais",    kg: 0.15 },
    ],
  },
];

function verdictFor(value, range) {
  if (!range) return { kind: "neutral", label: "—", color: "#5C6B5C" };
  const [lo, hi] = range;
  if (value < lo) return { kind: "low",  label: "Trop faible", color: "#C84B31", Icon: TrendingDown };
  if (value > hi) return { kind: "high", label: "Trop riche",  color: "#D97706", Icon: TrendingUp };
  return { kind: "ok", label: "Dans la cible", color: "#1C7A3C", Icon: CheckCircle2 };
}

export default function RationCalculator() {
  const [profileId, setProfileId] = useState("brebis_lact");
  const [rows, setRows] = useState([
    { ingredientId: "orge", kg: 1.0 },
  ]);
  const [showEnergy, setShowEnergy] = useState(true);
  const [showFiber, setShowFiber] = useState(false);
  const [customs, setCustoms] = useState([]); // user-added ingredients
  const [customOpen, setCustomOpen] = useState(false);
  const [customForm, setCustomForm] = useState({ label: "", mat: "", ufl: "", cb: "", price: "" });
  const [activeTemplateId, setActiveTemplateId] = useState(null);

  // Persistent custom prices: { [ingredientId]: price } stored in localStorage
  const PRICES_KEY = "mazra3ti:ration:prices";
  const CUSTOMS_KEY = "mazra3ti:ration:customs";
  const [customPrices, setCustomPrices] = useState(() => {
    try {
      const raw = typeof window !== "undefined" ? window.localStorage.getItem(PRICES_KEY) : null;
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });

  // Hydrate customs from localStorage
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(CUSTOMS_KEY);
      if (raw) setCustoms(JSON.parse(raw));
    } catch { /* ignore */ }
  }, []);

  // Persist customs
  useEffect(() => {
    try { window.localStorage.setItem(CUSTOMS_KEY, JSON.stringify(customs)); } catch { /* ignore */ }
  }, [customs]);

  // Persist custom prices
  useEffect(() => {
    try { window.localStorage.setItem(PRICES_KEY, JSON.stringify(customPrices)); } catch { /* ignore */ }
  }, [customPrices]);

  // Merge preset + custom ingredients, applying custom prices
  const ingredients = useMemo(() => {
    return [...PRESET_INGREDIENTS, ...customs].map((ing) => {
      if (customPrices[ing.id] != null && customPrices[ing.id] !== "") {
        return { ...ing, price: Number(customPrices[ing.id]), _priceOverridden: true };
      }
      return ing;
    });
  }, [customs, customPrices]);
  const ingredientsById = useMemo(() => Object.fromEntries(ingredients.map((i) => [i.id, i])), [ingredients]);
  const profile = ANIMAL_PROFILES.find((p) => p.id === profileId);

  const totals = useMemo(() => {
    let totalKg = 0, matSum = 0, uflSum = 0, cbSum = 0, costTotal = 0;
    for (const r of rows) {
      const ing = ingredientsById[r.ingredientId];
      const kg = Number(r.kg) || 0;
      if (!ing || kg <= 0) continue;
      totalKg += kg;
      matSum += kg * ing.mat;
      uflSum += kg * ing.ufl;
      cbSum += kg * ing.cb;
      costTotal += kg * ing.price;
    }
    if (totalKg <= 0) return { totalKg: 0, mat: 0, ufl: 0, cb: 0, costTotal: 0, costPerKg: 0 };
    return {
      totalKg,
      mat: matSum / totalKg,
      ufl: uflSum / totalKg,
      cb: cbSum / totalKg,
      costTotal,
      costPerKg: costTotal / totalKg,
    };
  }, [rows, ingredientsById]);

  const updateIngredientPrice = (ingredientId, price) => {
    setCustomPrices((prev) => {
      const next = { ...prev };
      if (price === "" || price == null) {
        delete next[ingredientId];
      } else {
        next[ingredientId] = price;
      }
      return next;
    });
  };

  const resetAllPrices = () => {
    setCustomPrices({});
    toast.success("Prix réinitialisés", { description: "Les prix par défaut sont restaurés." });
  };

  const addRow = () => {
    if (rows.length >= MAX_INGREDIENTS) {
      toast.info(`Maximum ${MAX_INGREDIENTS} ingrédients.`);
      return;
    }
    setRows([...rows, { ingredientId: ingredients[0]?.id, kg: 0.5 }]);
    setActiveTemplateId(null);
  };
  const removeRow = (i) => { setRows(rows.filter((_, idx) => idx !== i)); setActiveTemplateId(null); };
  const updateRow = (i, patch) => { setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r))); setActiveTemplateId(null); };

  const applyTemplate = (tpl) => {
    setProfileId(tpl.profileId);
    setRows(tpl.rows.map((r) => ({ ...r })));
    setActiveTemplateId(tpl.id);
    toast.success(`Modèle appliqué : ${tpl.label}`, {
      description: "Profil & ingrédients pré-remplis. Ajustez les kg selon vos besoins.",
    });
  };

  const submitCustom = () => {
    const { label, mat, ufl, cb, price } = customForm;
    if (!label.trim()) { toast.error("Nom d'ingrédient requis"); return; }
    const m = Number(mat), u = Number(ufl), c = Number(cb), p = Number(price);
    if (Number.isNaN(m) || Number.isNaN(u) || Number.isNaN(c) || Number.isNaN(p)) {
      toast.error("Valeurs numériques invalides");
      return;
    }
    const id = `custom_${Date.now()}`;
    setCustoms([...customs, { id, label: label.trim(), mat: m, ufl: u, cb: c, price: p }]);
    setCustomForm({ label: "", mat: "", ufl: "", cb: "", price: "" });
    setCustomOpen(false);
    toast.success("Ingrédient ajouté", { description: label });
  };

  const matVerdict = verdictFor(totals.mat, profile?.mat);
  const uflVerdict = verdictFor(totals.ufl, profile?.ufl);
  const cbVerdict  = verdictFor(totals.cb,  profile?.cb);

  return (
    <div data-testid="ration-calculator" className="space-y-6">
      {/* Header */}
      <div className="rounded-2xl border border-[#E2E4DF] bg-gradient-to-br from-[#1C3A27] to-[#142A1C] text-white p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#E05A3D]/20 grid place-items-center flex-shrink-0">
            <Calculator size={24} className="text-[#FF8C6E]" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-2xl font-bold">Calculateur de rations</h2>
            <p className="mt-1 text-sm text-white/70">Trouvez le bon mélange (jusqu'à {MAX_INGREDIENTS} ingrédients) qui atteint le % de protéines recommandé pour votre type d'animal.</p>
          </div>
        </div>
      </div>

      {/* Modèles préprogrammés */}
      <div data-testid="ration-templates" className="rounded-2xl border border-[#E2E4DF] bg-white p-5">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={18} className="text-[#E05A3D]" />
          <h3 className="font-display text-lg font-bold text-[#1C3A27]">Modèles préprogrammés</h3>
          <span className="text-xs text-[#5C6B5C] ms-1">— cliquez sur un modèle pour pré-remplir le calculateur</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {RATION_TEMPLATES.map((tpl) => {
            const isActive = activeTemplateId === tpl.id;
            return (
              <button
                key={tpl.id}
                type="button"
                onClick={() => applyTemplate(tpl)}
                data-testid={`template-${tpl.id}`}
                className={`group text-start rounded-xl border-2 p-3 transition-all hover:shadow-md hover:-translate-y-0.5 ${
                  isActive
                    ? "border-[#E05A3D] bg-[#E05A3D]/5 shadow-md"
                    : "border-[#E2E4DF] bg-white hover:border-[#1C3A27]/40"
                }`}
                style={isActive ? { boxShadow: `0 4px 12px ${tpl.color}30` } : undefined}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-2xl leading-none" aria-hidden="true">{tpl.icon}</span>
                  {isActive && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#E05A3D]">Actif</span>
                  )}
                </div>
                <p className="font-bold text-sm text-[#1C3A27] leading-tight" style={{ color: isActive ? tpl.color : undefined }}>
                  {tpl.label}
                </p>
                <p className="text-[11px] text-[#5C6B5C] mt-0.5 leading-snug">{tpl.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Profile selector + display toggles */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <Label className="text-[#5C6B5C] text-xs uppercase tracking-wider font-semibold">Type d'animal</Label>
          <Select value={profileId} onValueChange={(v) => { setProfileId(v); setActiveTemplateId(null); }}>
            <SelectTrigger data-testid="ration-profile" className="mt-2 h-12 bg-white border-[#E2E4DF] rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ANIMAL_PROFILES.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {profile && (
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className="px-3 py-1.5 rounded-full bg-[#E05A3D]/10 text-[#C84B31] font-bold border border-[#E05A3D]/30">
                Cible MAT : {profile.mat[0]}–{profile.mat[1]} %
              </span>
              {showEnergy && (
                <span className="px-3 py-1.5 rounded-full bg-[#1E6BBA]/10 text-[#1E6BBA] font-bold border border-[#1E6BBA]/30">
                  Cible UFL : {profile.ufl[0].toFixed(2)}–{profile.ufl[1].toFixed(2)} /kg
                </span>
              )}
              {showFiber && (
                <span className="px-3 py-1.5 rounded-full bg-[#7A5A1C]/10 text-[#7A5A1C] font-bold border border-[#7A5A1C]/30">
                  Cible CB : {profile.cb[0]}–{profile.cb[1]} %
                </span>
              )}
            </div>
          )}
        </div>
        <div className="bg-white border border-[#E2E4DF] rounded-2xl p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-[#5C6B5C] mb-2">Afficher en plus</p>
          <label className="flex items-center gap-2 cursor-pointer py-1.5">
            <input type="checkbox" data-testid="toggle-energy" checked={showEnergy} onChange={(e) => setShowEnergy(e.target.checked)} className="w-4 h-4 accent-[#1E6BBA]" />
            <span className="text-sm text-[#1C3A27]">⚡ Énergie (UFL)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer py-1.5">
            <input type="checkbox" data-testid="toggle-fiber" checked={showFiber} onChange={(e) => setShowFiber(e.target.checked)} className="w-4 h-4 accent-[#7A5A1C]" />
            <span className="text-sm text-[#1C3A27]">🌾 Fibres (CB)</span>
          </label>
        </div>
      </div>

      {/* Ingredient rows */}
      <div className="rounded-2xl border border-[#E2E4DF] bg-white overflow-hidden">
        <div className="px-5 py-3 bg-[#F0F0EB] flex items-center justify-between flex-wrap gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-[#5C6B5C]">Ingrédients ({rows.length}/{MAX_INGREDIENTS})</span>
          <div className="flex gap-2 flex-wrap">
            {Object.keys(customPrices).length > 0 && (
              <Button type="button" variant="ghost" size="sm" onClick={resetAllPrices} data-testid="reset-prices" className="h-8 text-xs text-[#C84B31] hover:bg-[#E05A3D]/10">
                ↺ Prix par défaut
              </Button>
            )}
            <Button type="button" variant="outline" size="sm" onClick={() => setCustomOpen(true)} data-testid="add-custom-ingredient" className="h-8 text-xs border-[#1C3A27]/30 hover:bg-[#1C3A27] hover:text-white">
              + Personnalisé
            </Button>
            <Button type="button" size="sm" onClick={addRow} disabled={rows.length >= MAX_INGREDIENTS} data-testid="add-ration-row" className="h-8 text-xs bg-[#E05A3D] hover:bg-[#C84B31] text-white">
              <Plus size={14} className="me-1" /> Ingrédient
            </Button>
          </div>
        </div>
        <div className="divide-y divide-[#E2E4DF]">
          {rows.map((r, i) => {
            const ing = ingredientsById[r.ingredientId];
            const isCustomPrice = ing?._priceOverridden;
            return (
              <div key={i} className="px-5 py-4 grid grid-cols-12 gap-3 items-center" data-testid={`ration-row-${i}`}>
                <div className="col-span-12 md:col-span-4">
                  <Select value={r.ingredientId} onValueChange={(v) => updateRow(i, { ingredientId: v })}>
                    <SelectTrigger className="h-11 bg-white border-[#E2E4DF] rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ingredients.map((ig) => (
                        <SelectItem key={ig.id} value={ig.id}>
                          {ig.label} · {ig.mat}% MAT
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-5 md:col-span-2">
                  <div className="relative">
                    <Input
                      type="number" min={0} step={0.1}
                      value={r.kg}
                      onChange={(e) => updateRow(i, { kg: e.target.value })}
                      data-testid={`ration-kg-${i}`}
                      className="h-11 bg-white border-[#E2E4DF] rounded-xl pe-10"
                    />
                    <span className="absolute end-3 top-1/2 -translate-y-1/2 text-xs text-[#5C6B5C] font-semibold">kg</span>
                  </div>
                </div>
                <div className="col-span-5 md:col-span-2">
                  <div className="relative">
                    <Input
                      type="number" min={0} step={0.01}
                      value={ing?.price ?? ""}
                      onChange={(e) => updateIngredientPrice(r.ingredientId, e.target.value)}
                      data-testid={`ration-price-${i}`}
                      title="Prix au kg — modifié pour cet ingrédient dans tout le calculateur"
                      className={`h-11 bg-white border rounded-xl pe-14 ${isCustomPrice ? "border-[#E05A3D] text-[#C84B31] font-bold" : "border-[#E2E4DF]"}`}
                    />
                    <span className="absolute end-3 top-1/2 -translate-y-1/2 text-[10px] text-[#5C6B5C] font-semibold">DT/kg</span>
                  </div>
                </div>
                <div className="col-span-1 md:col-span-3 text-xs text-[#5C6B5C]">
                  {ing && (
                    <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                      <span className="text-[#C84B31] font-bold">{ing.mat}%</span>
                      {showEnergy && <span className="text-[#1E6BBA]">⚡{ing.ufl.toFixed(2)}</span>}
                      {showFiber && <span className="text-[#7A5A1C]">🌾{ing.cb}%</span>}
                      <span className="text-[#1C3A27] font-bold">{(ing.price * (Number(r.kg) || 0)).toFixed(2)} DT</span>
                      {isCustomPrice && <span className="text-[10px] text-[#E05A3D] uppercase tracking-wider">modifié</span>}
                    </div>
                  )}
                </div>
                <div className="col-span-1 text-end">
                  <button onClick={() => removeRow(i)} data-testid={`remove-ration-${i}`} className="p-2 rounded-lg hover:bg-red-50 text-red-600">
                    <X size={16} />
                  </button>
                </div>
              </div>
            );
          })}
          {rows.length === 0 && (
            <div className="px-5 py-10 text-center text-[#5C6B5C] text-sm">
              Ajoutez un ingrédient pour démarrer le calcul.
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <ResultCard
          label="Protéines (MAT)"
          value={`${totals.mat.toFixed(1)} %`}
          target={profile ? `cible ${profile.mat[0]}–${profile.mat[1]}%` : ""}
          verdict={matVerdict}
          accent="#C84B31"
          testid="result-mat"
        />
        {showEnergy && (
          <ResultCard
            label="Énergie (UFL/kg)"
            value={totals.ufl.toFixed(2)}
            target={profile ? `cible ${profile.ufl[0].toFixed(2)}–${profile.ufl[1].toFixed(2)}` : ""}
            verdict={uflVerdict}
            accent="#1E6BBA"
            testid="result-ufl"
          />
        )}
        {showFiber && (
          <ResultCard
            label="Fibres (CB)"
            value={`${totals.cb.toFixed(1)} %`}
            target={profile ? `cible ${profile.cb[0]}–${profile.cb[1]}%` : ""}
            verdict={cbVerdict}
            accent="#7A5A1C"
            testid="result-cb"
          />
        )}
        <ResultCard
          label={`Coût total (${totals.totalKg.toFixed(1)} kg)`}
          value={`${totals.costTotal.toFixed(2)} DT`}
          target={totals.totalKg > 0 ? `${totals.costPerKg.toFixed(2)} DT / kg` : ""}
          verdict={{ kind: "neutral", label: "", color: "#1C3A27" }}
          accent="#1C3A27"
          testid="result-cost"
        />
      </div>

      {/* Tip */}
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex gap-3">
        <AlertTriangle size={20} className="flex-shrink-0 mt-0.5" />
        <div>
          <strong>Conseil :</strong> ces valeurs sont indicatives. Pour une ration précise, contactez Seifeddine (ingénieur agronome) via la messagerie Expert. Pour tout problème sanitaire, consultez un vétérinaire.
        </div>
      </div>

      {/* Custom ingredient dialog */}
      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="custom-ingredient-dialog">
          <DialogHeader>
            <DialogTitle>Ingrédient personnalisé</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>Nom</Label>
              <Input value={customForm.label} onChange={(e) => setCustomForm({ ...customForm, label: e.target.value })} placeholder="Ex: Mélange ferme" data-testid="custom-label" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>% Protéines (MAT)</Label>
                <Input type="number" min={0} step={0.1} value={customForm.mat} onChange={(e) => setCustomForm({ ...customForm, mat: e.target.value })} placeholder="18" data-testid="custom-mat" />
              </div>
              <div>
                <Label>UFL / kg</Label>
                <Input type="number" min={0} step={0.01} value={customForm.ufl} onChange={(e) => setCustomForm({ ...customForm, ufl: e.target.value })} placeholder="0.95" data-testid="custom-ufl" />
              </div>
              <div>
                <Label>% Fibres (CB)</Label>
                <Input type="number" min={0} step={0.1} value={customForm.cb} onChange={(e) => setCustomForm({ ...customForm, cb: e.target.value })} placeholder="12" data-testid="custom-cb" />
              </div>
              <div>
                <Label>Prix DT/kg</Label>
                <Input type="number" min={0} step={0.01} value={customForm.price} onChange={(e) => setCustomForm({ ...customForm, price: e.target.value })} placeholder="0.80" data-testid="custom-price" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomOpen(false)}>Annuler</Button>
            <Button onClick={submitCustom} data-testid="custom-submit" className="bg-[#1C3A27] hover:bg-[#142A1C] text-white">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResultCard({ label, value, target, verdict, accent, testid }) {
  const Icon = verdict.Icon;
  return (
    <div data-testid={testid} className="rounded-2xl border bg-white p-5 shadow-sm" style={{ borderColor: `${accent}30` }}>
      <p className="text-xs font-bold uppercase tracking-wider text-[#5C6B5C]">{label}</p>
      <p className="mt-2 font-display text-3xl font-bold" style={{ color: accent }}>{value}</p>
      {target && <p className="text-xs text-[#5C6B5C] mt-1">{target}</p>}
      {verdict.kind !== "neutral" && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold" style={{ color: verdict.color }}>
          {Icon && <Icon size={14} />}
          {verdict.label}
        </div>
      )}
    </div>
  );
}
